import { Component, ChangeDetectionStrategy, ViewEncapsulation, inject, OnInit, signal } from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-app/shared/fi-items-list/fi-remote-state-full-data-source';
import { TemplateModel } from '@fi-app/template/template.model';
import { DocumentTemplateService } from '../document-template.service';
import { ILoadDataArgs } from '@fi-app/shared/fi-items-list/fi-data-source-base.directive';
import { FiLocalSearchParameterModel } from '@fi-app/shared/fi-local-search2/fi-local-search-parameter.model';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiItemsListType } from '@fi-app/shared/fi-items-list/fi-items-list-type';
import { FiDisplayType } from '@fi-app/shared/types/display-type';
import { IPreviewImageCellValue } from '@fi-app/shared/fi-wrapper-table/fi-wrapper-table';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiImageSize } from '@fi-app/shared/types/image-size';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { SideListOptionType } from '@fi-app/shared/types/side-list-option-type';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { getObjectTypeNameTextId } from '@fi-app/shared/utils/object-type-name';
import { ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { getSideListOptionsBottomFn } from '@fi-shared/fi-items-list/side-list-options';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { ApplicationSettingsType } from '@fi-app/shared/types/application-settings-type';
import { AccessRightType } from '@fi-app/shared/types/access-right-type';
import { DocumentTemplateConfirmDeleteDialogComponent } from '../document-template-confirm-delete-dialog/document-template-confirm-delete-dialog.component';

@Component({
    selector: 'fi-document-template-list',
    templateUrl: './document-template-list.component.html',
    styleUrls: ['./document-template-list.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-document-template-list' },
})
export class DocumentTemplateListComponent extends FiRemoteStateFullDataSource<TemplateModel> implements OnInit {
    public defaultSortColumnName = 'Name';
    public defaultSortDirection = true;
    public objectType = ObjectTypes.Template;
    public itemsListType = FiItemsListType.Documents;

    private documentTemplatesService = inject(DocumentTemplateService);
    private fiMediaStorageService = inject(FiMediaStorageService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private accessToApplicationTypeService = inject(AccessToApplicationTypeService);

    protected itemsListActions = signal<IItemsListAction2[]>([]);
    protected optionsTop: ISideListOptionsItem[] = [
        { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('common.shared_with_me') },
        { value: SideListOptionType.New, text: this.appTextService.get('common.new_files') },
        { value: SideListOptionType.OnlyNotVisited, text: this.appTextService.get('mediabank.unread_files') },
        { value: SideListOptionType.CreatedBy, text: this.appTextService.get('common.created_by_me') },
        { value: SideListOptionType.Favorite, text: this.appTextService.get('common.my_favorites') },
        { value: SideListOptionType.Deleted, text: this.appTextService.get('common.trash') },
    ];
    protected readonly optionsBottom: ISideListOptionsItem[] = getSideListOptionsBottomFn(this.appTextService);

    private readonly restoreListAction: Readonly<IItemsListAction2> = {
        title: this.appTextService.get('common.restore'),
        onClick: () => this.restoreSelectedItems(),
    };

    private readonly moveToTrashListAction: Readonly<IItemsListAction2> = {
        title: this.appTextService.get('common.delete'),
        onClick: () => this.confirmMoveToTrashItems(),
    };

    private readonly deletePermanentlyListAction: Readonly<IItemsListAction2> = {
        title: this.appTextService.get('common.delete_permanently'),
        onClick: () => this.confirmDeletePermanentlySelectedItems(),
    };

    private readonly hasSuperAdminRight = this.accessToApplicationTypeService.HasAccessToApplicationType(
        ApplicationSettingsType.DocumentGlobal,
        AccessRightType.SuperAdmin,
    );

    public ngOnInit(): void {
        this.setItemsListActions();
        this.initSideList();
        this.subscribeOnListDisplayTypeChange();
    }

    private setItemsListActions(): void {
        if (this.filterType === SideListOptionType.Deleted) {
            this.itemsListActions.set([this.restoreListAction, this.deletePermanentlyListAction]);
        } else {
            this.itemsListActions.set([this.moveToTrashListAction]);
        }
    }

    protected isSuperAdminAccess(): boolean {
        return this.filterType === SideListOptionType.All;
    }

    private initSideList() {
        if (this.hasSuperAdminRight) {
            this.optionsTop = [
                {
                    value: SideListOptionType.All,
                    text: this.appTextService.get('common.all'),
                },
                ...this.optionsTop,
            ];
        }
    }

    public onSideListOptionsChanged(filterType: SideListOptionType): void {
        super.onSideListOptionsChanged(filterType);
        this.setItemsListActions();
    }

    private confirmMoveToTrashItems(): void {
        const selectedItems = this.selectedItems();
        if (selectedItems.some((item) => item.IsInUse)) {
            this.selectItemsWithUsagesToDelete(selectedItems);
            return;
        }

        this.fiItemsDeleteHelperService
            .commonDeleteConfirm(this.objectType, selectedItems)
            .then(() => this.moveToTrashSelectedItems(selectedItems));
    }

    private selectItemsWithUsagesToDelete(selectedItems: TemplateModel[]): void {
        this.fiDialogsService
            .custom({
                headerText: this.appTextService.get('template.select_templates'),
                yesButtonText: this.appTextService.get('common.delete'),
                contentComponentRef: DocumentTemplateConfirmDeleteDialogComponent,
                contentComponentData: {
                    selectedItems: selectedItems,
                },
                config: {
                    panelClass: ['fi-rounded-dialog', 'dialog-confirm-delete-template'],
                    maxWidth: '90%',
                    width: '800px',
                    closeOnNavigation: true,
                },
            })
            .onYesButtonClick((result: TemplateModel[]) => {
                this.moveToTrashSelectedItems(result);
            });
    }

    private moveToTrashSelectedItems(itemsToDelete: TemplateModel[]): void {
        this.documentTemplatesService.DeleteTemplates(itemsToDelete.map((item) => item.Id)).then(() => {
            this.removeData(itemsToDelete);
            this.clearSelectedItems();
            this.showItemsMovedToTrashSnackbar(itemsToDelete.length);
        });
    }

    private showItemsMovedToTrashSnackbar(itemsCount: number): void {
        const snackbarTextMessage =
            itemsCount +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, itemsCount !== 1)) +
            ' ' +
            this.appTextService.get('history.moved_to_trash').toLowerCase();

        this.snackBar.showSnackbar({
            textMessage: snackbarTextMessage,
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    private restoreSelectedItems(): void {
        this.fiItemsDeleteHelperService.restoreItems({
            itemsToRestore: this.selectedItems(),
            objectType: this.objectType,
            restoreFn: () =>
                this.documentTemplatesService.RestoreTemplates(this.selectedItems().map((item) => item.Id)),
            afterRestoreFn: (restoredItems) => {
                this.removeData(restoredItems);
                this.showItemsRestoredSnackbar(restoredItems.length);
                this.clearSelectedItems();
            },
        });
    }

    private showItemsRestoredSnackbar(itemsCount: number): void {
        const snackbarTextMessage =
            itemsCount +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, itemsCount !== 1)) +
            ' ' +
            this.appTextService.get('history.restored').toLowerCase();

        this.snackBar.showSnackbar({
            textMessage: snackbarTextMessage,
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    private confirmDeletePermanentlySelectedItems(): void {
        this.fiItemsDeleteHelperService
            .commonDeleteConfirm(
                this.objectType,
                this.selectedItems(),
                this.appTextService.get('template.delete_permanently_confirm'),
            )
            .then(() => this.deletePermanentlySelectedItems());
    }

    private deletePermanentlySelectedItems(): void {
        const selectedItems = this.selectedItems();
        this.documentTemplatesService.DeleteTemplates(selectedItems.map((item) => item.Id)).then(() => {
            this.removeData(selectedItems);
            this.clearSelectedItems();
            this.showItemsDeletedPermanentlySnackbar(selectedItems.length);
        });
    }

    private showItemsDeletedPermanentlySnackbar(itemsCount: number): void {
        const snackbarTextMessage =
            itemsCount +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, itemsCount !== 1)) +
            ' ' +
            this.appTextService.get('history.deleted').toLowerCase();

        this.snackBar.showSnackbar({
            textMessage: snackbarTextMessage,
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    protected getTemplateImageCellValue(templateModel: TemplateModel): IPreviewImageCellValue {
        return {
            image: this.fiMediaStorageService.getMediaFileUrl(
                {
                    PhysicalFileName: templateModel.PreviewFileName,
                    Extension: FiAppSettings.DefaultMediaBankImageExtension,
                    ChangedOn:
                        typeof templateModel.ChangedOn === 'string'
                            ? templateModel.ChangedOn
                            : templateModel.ChangedOn.toISOString(),
                },
                { size: this.isDisplayTypeListGrid() ? FiImageSize.Small : FiImageSize.Thumbnail },
            ),
            imageLarge: '',
            iconClass: '',
        };
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return this.documentTemplatesService.getTemplatesListData(parameter);
    }
}
